/**
 * IdentitySection (P10 §4.2)
 * Bloques horizontales: cards lado a lado con InfoRow (justify-between).
 * Las cards son estrechas → InfoRow funciona bien sin gap excesivo.
 */
import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Image as ImageIcon, ExternalLink, CheckCircle2, Clock, AlertCircle, MapPin, XCircle, Eye, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Can } from '@/components/auth/Can';
import { UNE_PERM } from '@/constants';
import { useExpedienteIdentity } from '../../hooks';
import { AuditSection } from '../AuditSection';
import type {
  ExpedienteIdentityDocument,
  ExpedienteIdentityAddress,
} from '@/types/expedientes.types';

interface IdentitySectionProps {
  directoryId: string;
  enabled: boolean;
}

export function IdentitySection({ directoryId, enabled }: IdentitySectionProps) {
  const { identity, isLoading, isError } = useExpedienteIdentity(directoryId, enabled);

  if (!enabled) return null;
  if (isLoading) return <div className="p-4 text-sm text-muted-foreground">Cargando identidad…</div>;
  if (isError || !identity) {
    return <div className="p-4 text-sm text-red-600 dark:text-red-400">Error al cargar la identidad.</div>;
  }

  const { personal_data, kyc, address, compliance_lists, risk_profile, documents } = identity;
  const dirShort = personal_data.directory_id.slice(0, 8);
  const hasIdData = personal_data.curp || personal_data.rfc || personal_data.date_of_birth || personal_data.sex || personal_data.nationality;

  // Clasificar documentos: identidad (passport, INE, selfie...) vs KYC (summary, result...)
  const identityDocs = documents.filter((d) => isIdentityDoc(d));
  const kycDocs = documents.filter((d) => !isIdentityDoc(d));

  return (
    <div className="p-4 space-y-3">
      {/* Fila 1: Personal | KYC */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Block label="Datos personales" color="emerald">
          <InfoGrid>
            <InfoRow label="Nombre" value={personal_data.full_name} />
            <InfoRow label="Teléfono" value={personal_data.phone} />
            <InfoRow label="Email" value={personal_data.email} truncate />
            <InfoRow label="Directory ID" value={`DIR-${dirShort}`} />
            <InfoRow
              label="Cliente desde"
              value={personal_data.client_since
                ? `${formatDate(personal_data.client_since)} (${personal_data.wallet_source ?? '—'})`
                : null}
            />
            <InfoRow label="PEP" value={humanizePep(personal_data.pep_level)} />
          </InfoGrid>
        </Block>

        <Block label="Verificación KYC" color="purple">
          {kyc ? (
            <>
              <KycBanner status={kyc.status} verifiedAt={kyc.verified_at} />
              <InfoGrid>
                <InfoRow label="Documento" value={kyc.primary_document_type?.toUpperCase() ?? null} />
                <InfoRow label="Número" value={kyc.document_number} />
                <InfoRow label="ID verificación" value={kyc.verification_id} truncate />
                <InfoRow label="Expedido" value={formatDate(kyc.document_issue_date)} />
                <InfoRow label="Vence" value={formatDate(kyc.document_expires_at)} />
                <InfoRow
                  label="Score anti-fraude"
                  value={risk_profile?.score != null
                    ? `${risk_profile.score}${risk_profile.level ? ` · ${risk_profile.level}` : ''}`
                    : null}
                />
                <InfoRow label="País emisor" value={humanizeCountry(kyc.document_country_id)} />
                <InfoRow label="Estado emisor" value={kyc.document_issue_state} />
              </InfoGrid>
            </>
          ) : (
            <p className="text-sm text-muted-foreground py-1">Sin verificación KYC registrada.</p>
          )}
          {kycDocs.length > 0 && (
            <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
              {kycDocs.map((doc) => (
                <DocumentRow key={doc.key} doc={doc} />
              ))}
            </div>
          )}
        </Block>
      </div>

      {/* Fila 2: Identificación | Cumplimiento + Dirección */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {hasIdData && (
          <Block label="Documentos oficiales" color="amber">
            <InfoGrid>
              <InfoRow label="CURP" value={personal_data.curp} />
              <InfoRow label="RFC" value={personal_data.rfc} />
              <InfoRow label="Nacimiento" value={formatDate(personal_data.date_of_birth)} />
              <InfoRow label="Sexo" value={humanizeSex(personal_data.sex)} />
              <InfoRow label="Nacionalidad" value={humanizeCountry(personal_data.nationality)} />
            </InfoGrid>
            {identityDocs.length > 0 && (
              <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                {identityDocs.map((doc) => (
                  <DocumentRow key={doc.key} doc={doc} />
                ))}
              </div>
            )}
          </Block>
        )}

        <div className="space-y-3">
          {compliance_lists && (
            <Block label="Listas de cumplimiento" color="rose">
              <div className="flex flex-wrap gap-1.5 py-0.5">
                <CompliancePill label="Fiado" approved={compliance_lists.fiado_list_approved} />
                <CompliancePill label="CNBV" approved={compliance_lists.cnbv_list_approved} />
                <CompliancePill label="Lista negra" approved={compliance_lists.black_list_approved} />
              </div>
            </Block>
          )}
          {address && (
            <Block label="Dirección" color="blue">
              <AddressInline address={address} />
            </Block>
          )}
        </div>
      </div>

      {/* Historial de accesos al expediente — sub-sección al final */}
      <Can permission={UNE_PERM.EXPEDIENTE_AUDIT}>
        <details className="mt-2 rounded-md border border-border bg-card/50">
          <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
            Historial de accesos al expediente
          </summary>
          <div className="border-t border-border">
            <AuditSection directoryId={personal_data.directory_id} enabled />
          </div>
        </details>
      </Can>
    </div>
  );
}

// ── layout ───────────────────────────────────────────────────────────────

const BLOCK_COLORS: Record<string, { border: string; text: string; bg: string }> = {
  emerald: { border: 'border-l-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50/60 dark:bg-emerald-950/20' },
  purple:  { border: 'border-l-purple-500',  text: 'text-purple-600 dark:text-purple-400',  bg: 'bg-purple-50/60 dark:bg-purple-950/20' },
  amber:   { border: 'border-l-amber-500',   text: 'text-amber-600 dark:text-amber-400',    bg: 'bg-amber-50/60 dark:bg-amber-950/20' },
  rose:    { border: 'border-l-rose-500',     text: 'text-rose-600 dark:text-rose-400',      bg: 'bg-rose-50/60 dark:bg-rose-950/20' },
  blue:    { border: 'border-l-blue-500',     text: 'text-blue-600 dark:text-blue-400',      bg: 'bg-blue-50/60 dark:bg-blue-950/20' },
  slate:   { border: 'border-l-slate-500',    text: 'text-slate-500 dark:text-slate-400',    bg: 'bg-slate-50/60 dark:bg-slate-950/20' },
};

function Block({ label, color, children }: { label: string; color: string; children: React.ReactNode }) {
  const fallback = { border: 'border-l-slate-500', text: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-50/60 dark:bg-slate-950/20' };
  const c = BLOCK_COLORS[color] ?? fallback;
  return (
    <div className={`border-l-2 ${c.border} ${c.bg} rounded-r-md px-3 py-2`}>
      <p className={`text-xs uppercase font-semibold tracking-wider mb-1.5 ${c.text}`}>{label}</p>
      {children}
    </div>
  );
}

function InfoRow({ label, value, truncate: shouldTruncate }: { label: string; value: string | null | undefined; truncate?: boolean }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-[2px] items-baseline">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span
        className={`text-sm font-semibold text-foreground ${shouldTruncate ? 'truncate' : ''}`}
        title={shouldTruncate && value ? value : undefined}
      >
        {value ?? '—'}
      </span>
    </div>
  );
}

function InfoGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
      {children}
    </div>
  );
}

// ── KYC banner ───────────────────────────────────────────────────────────

function KycBanner({ status, verifiedAt }: { status: string | null; verifiedAt: string | null }) {
  const upper = (status ?? '').toUpperCase();
  const isApproved = upper === 'VERIFIED' || upper === 'APPROVED' || upper === 'APROBADO';
  const isPending = upper === 'PENDING' || upper === 'PENDIENTE';
  const isRejected = upper === 'REJECTED' || upper === 'RECHAZADO';

  const toneClass = isApproved
    ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300'
    : isPending
    ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
    : isRejected
    ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300'
    : 'border-border bg-muted text-muted-foreground';

  const Icon = isApproved ? CheckCircle2 : isPending ? Clock : isRejected ? AlertCircle : Clock;
  const statusLabel = isApproved ? 'Aprobado' : isPending ? 'Pendiente' : isRejected ? 'Rechazado' : (status ?? 'Sin estado');

  return (
    <div className={`border rounded-md px-3 py-1.5 flex items-center gap-2 text-sm mb-1 ${toneClass}`}>
      <Icon size={16} className="shrink-0" />
      <span className="font-semibold">{statusLabel}</span>
      {verifiedAt && (
        <span className="text-xs opacity-80">· {formatDateTime(verifiedAt)}</span>
      )}
    </div>
  );
}

// ── compliance ───────────────────────────────────────────────────────────

function CompliancePill({ label, approved }: { label: string; approved: boolean | null }) {
  const Icon = approved === true ? CheckCircle2 : approved === false ? XCircle : Clock;
  const toneClass = approved === true
    ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300'
    : approved === false
    ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300'
    : 'border-border bg-muted text-muted-foreground';
  return (
    <div className={`inline-flex items-center gap-1 border rounded-full px-2.5 py-1 text-xs font-medium ${toneClass}`}>
      <Icon size={12} className="shrink-0" />
      <span>{label}</span>
    </div>
  );
}

// ── dirección ────────────────────────────────────────────────────────────

function AddressInline({ address }: { address: ExpedienteIdentityAddress }) {
  const line1 = [address.street, address.address_number, address.internal_number && `int. ${address.internal_number}`]
    .filter(Boolean)
    .join(' ');
  const line2 = [address.neighborhood, address.postal_code && `CP ${address.postal_code}`].filter(Boolean).join(' · ');
  const line3 = [address.municipality, address.region].filter(Boolean).join(', ');
  return (
    <div className="flex items-start gap-2 py-0.5 text-sm">
      <MapPin size={14} className="text-muted-foreground shrink-0 mt-0.5" />
      <div className="flex-1">
        <span className="font-semibold text-foreground">{line1 || '—'}</span>
        {(line2 || line3) && (
          <p className="text-muted-foreground text-xs">
            {[line2, line3].filter(Boolean).join(' — ')}
          </p>
        )}
        {(address.is_principal || address.status) && (
          <div className="flex gap-1 mt-0.5">
            {address.is_principal && <Badge variant="info">Principal</Badge>}
            {address.status && <Badge>{address.status}</Badge>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── documentos ───────────────────────────────────────────────────────────

function DocumentRow({ doc }: { doc: ExpedienteIdentityDocument }) {
  const [showModal, setShowModal] = useState(false);
  const isPdf = doc.key.toLowerCase().endsWith('.pdf');
  const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(doc.key);
  const isVideo = /\.(mp4|mov|mkv|webm)$/i.test(doc.key);

  const docLabel = doc.type_of_document
    ? capitalize(doc.type_of_document)
    : prettyDocLabel(doc.key);
  const docSuffix = extractDocSuffix(doc.key);

  const ocrParts = [
    doc.ocr_full_name,
    doc.document_number,
    doc.ocr_date_of_birth ? formatDate(doc.ocr_date_of_birth) : null,
  ].filter(Boolean);

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border hover:bg-muted/50 transition">
        {isPdf || isVideo ? (
          <FileText size={14} className="text-muted-foreground shrink-0" />
        ) : (
          <ImageIcon size={14} className="text-muted-foreground shrink-0" />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-foreground">{docLabel}</span>
            {docSuffix && <span className="text-xs text-muted-foreground">· {docSuffix}</span>}
            {doc.verification_date && <Badge variant="success">Verificado</Badge>}
          </div>
          {ocrParts.length > 0 && (
            <p className="text-xs text-muted-foreground leading-tight">
              OCR: {ocrParts.join(' · ')}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setShowModal(true)}
            disabled={!doc.signed_url}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition disabled:opacity-40 disabled:cursor-not-allowed"
            title="Vista previa"
          >
            <Eye size={16} />
          </button>
          {doc.signed_url && (
            <a
              href={doc.signed_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition"
              title="Descargar"
            >
              <ExternalLink size={16} />
            </a>
          )}
        </div>
      </div>

      {showModal && doc.signed_url && (
        <DocPreviewModal
          url={doc.signed_url}
          label={docLabel}
          isPdf={isPdf}
          isImage={isImage}
          isVideo={isVideo}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

// ── modal vista previa ──────────────────────────────────────────────────

function DocPreviewModal({
  url, label, isPdf, isImage, isVideo, onClose,
}: {
  url: string; label: string; isPdf: boolean; isImage: boolean; isVideo: boolean; onClose: () => void;
}) {
  const handleBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative bg-card border border-border rounded-lg shadow-xl max-w-3xl w-[90vw] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm font-semibold text-foreground truncate">{label}</span>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition"
          >
            <X size={18} />
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center">
          {isImage && <img src={url} alt={label} className="max-w-full max-h-[70vh] rounded object-contain" />}
          {isPdf && <iframe src={url} title={label} className="w-full h-[70vh] rounded" />}
          {isVideo && <video src={url} controls className="max-w-full max-h-[70vh] rounded" />}
          {!isImage && !isPdf && !isVideo && (
            <p className="text-sm text-muted-foreground">Vista previa no disponible.</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── clasificación de documentos ──────────────────────────────────────────

const IDENTITY_KEYWORDS = ['passport', 'ine', 'id', 'front', 'back', 'selfie', 'liveness', 'video', 'frente', 'reverso', 'credential', 'national'];

function isIdentityDoc(doc: ExpedienteIdentityDocument): boolean {
  const type = (doc.type_of_document ?? '').toLowerCase();
  if (IDENTITY_KEYWORDS.some((kw) => type.includes(kw))) return true;
  const keyLower = doc.key.toLowerCase();
  return IDENTITY_KEYWORDS.some((kw) => keyLower.includes(kw));
}

// ── humanización ────────────────────────────────────────────────────────

const COUNTRY_NUMERIC: Record<string, string> = {
  '484': 'México', '840': 'Estados Unidos', '124': 'Canadá', '032': 'Argentina',
  '076': 'Brasil', '152': 'Chile', '170': 'Colombia', '188': 'Costa Rica',
  '214': 'Rep. Dominicana', '218': 'Ecuador', '222': 'El Salvador', '320': 'Guatemala',
  '340': 'Honduras', '558': 'Nicaragua', '591': 'Panamá', '604': 'Perú',
  '858': 'Uruguay', '862': 'Venezuela', '724': 'España', '826': 'Reino Unido',
  '250': 'Francia', '276': 'Alemania', '380': 'Italia', '392': 'Japón',
  '156': 'China', '356': 'India', '410': 'Corea del Sur',
};

const COUNTRY_ALPHA: Record<string, string> = {
  MX: 'México', US: 'Estados Unidos', CA: 'Canadá', AR: 'Argentina',
  BR: 'Brasil', CL: 'Chile', CO: 'Colombia', CR: 'Costa Rica',
  DO: 'Rep. Dominicana', EC: 'Ecuador', SV: 'El Salvador', GT: 'Guatemala',
  HN: 'Honduras', NI: 'Nicaragua', PA: 'Panamá', PE: 'Perú',
  UY: 'Uruguay', VE: 'Venezuela', ES: 'España', GB: 'Reino Unido',
  FR: 'Francia', DE: 'Alemania', IT: 'Italia', JP: 'Japón',
  CN: 'China', IN: 'India', KR: 'Corea del Sur',
};

function humanizeCountry(code: string | null | undefined): string | null {
  if (!code) return null;
  const trimmed = code.trim();
  return COUNTRY_NUMERIC[trimmed] ?? COUNTRY_ALPHA[trimmed.toUpperCase()] ?? trimmed;
}

function humanizeSex(value: string | null | undefined): string | null {
  if (!value) return null;
  const upper = value.trim().toUpperCase();
  if (upper === 'F' || upper === 'FEMALE' || upper === 'FEMENINO') return 'Femenino';
  if (upper === 'M' || upper === 'MALE' || upper === 'MASCULINO') return 'Masculino';
  if (upper === 'X' || upper === 'OTHER' || upper === 'OTRO') return 'Otro';
  return value;
}

function humanizePep(value: string | null | undefined): string | null {
  if (!value) return null;
  const upper = value.trim().toUpperCase();
  if (upper === 'NO_PEP' || upper === 'NO' || upper === 'NONE') return 'No';
  if (upper === 'PEP' || upper === 'YES' || upper === 'SI') return 'Sí — PEP';
  return value;
}

// ── utilidades ───────────────────────────────────────────────────────────

function prettyDocLabel(key: string): string {
  const base = key.replace(/\.(png|jpg|jpeg|gif|webp|pdf|mp4|mov|webm)$/i, '');
  const parts = base.split(/[_-]/).filter(Boolean);
  const meaningful = parts.filter((p) => !/^[0-9a-fA-F]{3,}$/.test(p));
  if (meaningful.length === 0) return 'Documento';
  return meaningful
    .map((p) => {
      const upper = p.toUpperCase();
      if (upper === 'FRONT') return 'Frente';
      if (upper === 'BACK') return 'Reverso';
      if (upper === 'SELFIE') return 'Selfie';
      if (upper === 'LIVENESS') return 'Liveness';
      if (upper === 'VIDEO') return 'Video';
      if (upper === 'SUMMARY') return 'Resumen';
      if (upper === 'RESULT') return 'Resultado';
      if (upper === 'PDF') return 'PDF';
      return capitalize(p);
    })
    .join(' · ');
}

function extractDocSuffix(key: string): string | null {
  const base = key.replace(/\.(png|jpg|jpeg|gif|webp|pdf|mp4|mov|webm)$/i, '');
  const parts = base.split(/[_-]/).filter(Boolean);
  const meaningful = parts.filter((p) => !/^[0-9a-fA-F]{3,}$/.test(p));
  if (meaningful.length <= 1) return null;
  const last = meaningful[meaningful.length - 1]!;
  const upper = last.toUpperCase();
  if (upper === 'FRONT') return 'Frente';
  if (upper === 'BACK') return 'Reverso';
  if (upper === 'SELFIE') return 'Selfie';
  if (upper === 'LIVENESS') return 'Liveness';
  if (upper === 'VIDEO') return 'Video';
  if (upper === 'SUMMARY') return 'Resumen';
  if (upper === 'RESULT') return 'Resultado';
  return capitalize(last);
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('es-MX');
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
